import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import AdminDashboard from './components/admin/AdminDashboard';
import ClientDashboard from './components/client/ClientDashboard';
import DownloadPage from './components/client/DownloadPage';
import KaraokeMaker from './components/client/KaraokeMaker';
import AudioGenerator from './components/client/AudioGenerator';
import VideoGenerator from './components/client/VideoGenerator';
import AudioAdminDashboard from './components/admin/AudioAdminDashboard';
import VideoCompressor from './components/client/VideoCompressor';
import PaymentSuccess from './components/payment/PaymentSuccess';
import PaymentCancel from './components/payment/PaymentCancel';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import LandingPage from './components/LandingPage';
import DemoTranslator from './components/DemoTranslator';

const API_URL = 'https://fbapi.tdidigital.io';

// ── LoginPage wrapper avec accès à handleLogin ────────────────────────────────
function LoginPage() {
  const [isAuth, setIsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const userType = localStorage.getItem('user_type');
    if (token) {
      try {
        const response = await fetch(`${API_URL}/health`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsAuth(true);
          setIsAdmin(userType === 'admin');
        } else {
          localStorage.clear();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData) => {
    setIsAuth(true);
    setIsAdmin(userData.user_type === 'admin');
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuth(false);
    setIsAdmin(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (isAuth) {
    if (isAdmin) return <AdminDashboard onLogout={handleLogout} />;
    return <ClientDashboard onLogout={handleLogout} />;
  }

  return <Login onLogin={handleLogin} />;
}

// ── SignupPage wrapper — même logique que LoginPage ───────────────────────────
function SignupPage() {
  const [isAuth, setIsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const userType = localStorage.getItem('user_type');
    if (token) {
      try {
        const response = await fetch(`${API_URL}/health`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsAuth(true);
          setIsAdmin(userType === 'admin');
        } else {
          localStorage.clear();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData) => {
    setIsAuth(true);
    setIsAdmin(userData.user_type === 'admin');
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuth(false);
    setIsAdmin(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (isAuth) {
    if (isAdmin) return <AdminDashboard onLogout={handleLogout} />;
    return <ClientDashboard onLogout={handleLogout} />;
  }

  return <Signup onLogin={handleLogin} />;
}

// ── HomePage — landing publique ───────────────────────────────────────────────
function HomePage() {
  const [isAuth, setIsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const userType = localStorage.getItem('user_type');
    if (token) {
      try {
        const response = await fetch(`${API_URL}/health`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsAuth(true);
          setIsAdmin(userType === 'admin');
        } else {
          localStorage.clear();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuth(false);
    setIsAdmin(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  // Si déjà connecté → dashboard direct
  if (isAuth) {
    if (isAdmin) return <AdminDashboard onLogout={handleLogout} />;
    return <ClientDashboard onLogout={handleLogout} />;
  }

  // Sinon → landing publique
  return <LandingPage />;
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <Router>
      <DemoTranslator />
      <Routes>
        <Route path="/"                element={<HomePage />} />
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/signup"          element={<SignupPage />} />
        <Route path="/download"        element={<DownloadPage />} />
        <Route path="/mp3"             element={<KaraokeMaker />} />
        <Route path="/audio"           element={<AudioGenerator />} />
        <Route path="/admin"           element={<AudioAdminDashboard />} />
        <Route path="/compressed"      element={<VideoCompressor />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel"  element={<PaymentCancel />} />
        <Route path="/privacy"         element={<PrivacyPolicy />} />
        <Route path="/terms"           element={<TermsOfService />} />
        <Route path="/video"           element={<VideoGenerator />} />
      </Routes>
    </Router>
  );
}